import React, { Dispatch, SetStateAction } from 'react';

import { SelectInput } from '@amzn/imdb-shared-meridian-components/components/InputForm/SelectInput/SelectInput';
import { TextareaInput } from '@amzn/imdb-shared-meridian-components/components/InputForm/TextareaInput/TextareaInput';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import ControlGroup from '@amzn/meridian/control-group';
import Heading from '@amzn/meridian/heading';
import Input from '@amzn/meridian/input';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';

import { ListDetails, ListFlag, ListFlagReasonCode, ListState } from '../../../listoramaAdmin-api/generated-src';
import { SelectedLists } from '../../features/UserDetailsSection/components/UserListsDataTable/UserListsDataTable';
import { ListActionButtonConfig, ListButtonId } from '../ListActionButtons/ListActionButtons';

export const FLAG_NOTE_HINT = 'Optional note. Reference tickets by ID, do not paste customer content.';

export interface ListModalData {
    isOpen: boolean;
    buttonProps?: ListActionButtonConfig<ListButtonId>;
}

export interface ListEditData {
    state?: ListState;
    flag?: ListFlag;
    // Structured context for flag changes; accepted by the admin API today and persisted
    // once flag audit history (Story 1.4, DISCO-21397) deploys.
    reasonCode?: ListFlagReasonCode;
    note?: string;
    ticketId?: string;
}

interface ListActionModalProps {
    modalData: ListModalData;
    setModalData: Dispatch<SetStateAction<ListModalData>>;
    listEditData: ListEditData;
    setListEditData: Dispatch<SetStateAction<ListEditData>>;
    list?: ListDetails;
    selectedLists?: SelectedLists;
}

export const ListActionModal: React.FC<ListActionModalProps> = (props: ListActionModalProps) => {
    const { list, selectedLists, modalData, setModalData, listEditData, setListEditData } = props;

    const onModalClose = () => {
        setModalData({ isOpen: false });
        setListEditData((prevState) => ({
            ...prevState,
            flag: undefined,
            reasonCode: undefined,
            note: undefined,
            ticketId: undefined
        }));
    };

    return (
        <Modal width={400} onClose={onModalClose} title={modalData.buttonProps?.text} open={modalData.isOpen}>
            {modalData.buttonProps ? (
                createModalContent(list, selectedLists, modalData.buttonProps, listEditData, setListEditData)
            ) : (
                <></>
            )}
        </Modal>
    );
};

interface FlagSelectInputProps {
    list: ListDetails | undefined;
    buttonId: ListButtonId;
    listEditData: ListEditData;
    setListEditData: Dispatch<SetStateAction<ListEditData>>;
}

const FlagSelectInput = (props: FlagSelectInputProps) => {
    const { list, buttonId, listEditData, setListEditData } = props;

    const isFlagInDropdown = (flag: ListFlag) => {
        switch (buttonId) {
            case 'add_list_flag':
                return list && list.flags.indexOf(flag) < 0;
            case 'remove_list_flag':
                return list && list.flags.indexOf(flag) >= 0;
        }
    };

    const options = Object.keys(ListFlag)
        .filter((key) => (list ? isFlagInDropdown(ListFlag[key]) : true))
        .map((key) => ({ key, value: ListFlag[key], label: ListFlag[key] }));
    return (
        <SelectInput
            id={buttonId}
            placeholder='Select flag'
            value={listEditData.flag!}
            onChange={(flag) => setListEditData((prevState) => ({ ...prevState, flag }))}
            options={options}
        />
    );
};

interface FlagReasonContextInputsProps {
    listEditData: ListEditData;
    setListEditData: Dispatch<SetStateAction<ListEditData>>;
}

const REASON_CODE_OPTIONS = Object.keys(ListFlagReasonCode).map((key) => ({
    key,
    value: ListFlagReasonCode[key],
    label: ListFlagReasonCode[key]
}));

const FlagReasonContextInputs = (props: FlagReasonContextInputsProps) => {
    const { listEditData, setListEditData } = props;

    return (
        <>
            <SelectInput
                id='flag_reason_code'
                placeholder='Select reason (optional)'
                value={listEditData.reasonCode ?? ''}
                onChange={(reasonCode) =>
                    setListEditData((prevState) => ({ ...prevState, reasonCode: reasonCode || undefined }))
                }
                options={REASON_CODE_OPTIONS}
            />
            <TextareaInput
                id='flag_note'
                value={listEditData.note ?? ''}
                onChange={(note) => setListEditData((prevState) => ({ ...prevState, note: note || undefined }))}
                rows={3}
                helperText={FLAG_NOTE_HINT}
            />
            <Input
                id='flag_ticket_id'
                type='text'
                placeholder='Ticket ID (optional)'
                value={listEditData.ticketId ?? ''}
                onChange={(ticketId) =>
                    setListEditData((prevState) => ({ ...prevState, ticketId: ticketId || undefined }))
                }
            />
        </>
    );
};

const createInput = (
    list: ListDetails | undefined,
    buttonProps: ListActionButtonConfig<ListButtonId>,
    listEditData: ListEditData,
    setListEditData: Dispatch<SetStateAction<ListEditData>>
): React.ReactNode => {
    switch (buttonProps.id) {
        case 'edit_list_state':
            return (
                <ControlGroup
                    legend='Select state'
                    value={listEditData.state}
                    onChange={(state) => setListEditData((prevState) => ({ ...prevState, state }))}
                >
                    {Object.keys(ListState).map((key) => (
                        <RadioButton key={key} value={ListState[key]}>
                            {ListState[key]}
                        </RadioButton>
                    ))}
                </ControlGroup>
            );
        case 'add_list_flag':
        case 'remove_list_flag':
            return (
                <>
                    <FlagSelectInput
                        list={list}
                        buttonId={buttonProps.id}
                        listEditData={listEditData}
                        setListEditData={setListEditData}
                    />
                    <FlagReasonContextInputs listEditData={listEditData} setListEditData={setListEditData} />
                </>
            );
    }
};

const createModalContent = (
    list: ListDetails | undefined,
    selectedLists: SelectedLists | undefined,
    buttonProps: ListActionButtonConfig<ListButtonId>,
    listEditData: ListEditData,
    setListEditData: Dispatch<SetStateAction<ListEditData>>
): React.ReactNode => (
    <Column spacing='200'>
        {selectedLists && <Heading level={4}>{createSelectedListsMessage(selectedLists)}</Heading>}
        {createInput(list, buttonProps, listEditData, setListEditData)}
        <ModalFooter>
            <Row alignmentHorizontal='end' widths='fit'>
                <Button type='primary' size='small' onClick={() => buttonProps.onConfirm(listEditData)}>
                    Confirm
                </Button>
            </Row>
        </ModalFooter>
    </Column>
);

const createSelectedListsMessage = (selectedLists: SelectedLists) => {
    const lists = Object.keys(selectedLists).filter((listId) => selectedLists[listId]);
    return lists.length > 1 ? `Editing ${lists.length} lists...` : `Editing ${lists.length} list...`;
};
